import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../Dashboard/Sidebar";
import Header from "../Dashboard/Header";
import Footer from "../Dashboard/Footer";
import Step1_ProfileName from "../Dashboard/Step1_ProfileName";
import Step3_Configure from "../Dashboard/Step3_Configure";

import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, Check
} from "lucide-react";

// Custom primary color for DCode theme
// **UPDATED COLORS:**
const DCODE_PRIMARY = "#75c4c0";
const DCODE_DANGER = "#f88381";

function QualityGate() {
  const navigate = useNavigate();

  const { profileId } = useParams();
  const isEditMode = !!profileId;
  const originalProfileId = profileId;

  const projectId = localStorage.getItem("selectedProjectId");

  // Layout states (keeping existing logic for context)
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState("success");

  // Wizard states
  const [currentStep, setCurrentStep] = useState(0);
  const [profileName, setProfileName] = useState("");

  // Selected metric: "fitness" | "smell" | null
  const [selectedMetric, setSelectedMetric] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Threshold values - Ensure initial values are what you want for new profiles
  const initialThresholds = {
    fitness_absolute: { enabled: false, value: 0.8 },
    fitness_relative: { enabled: false, Y: 1, L: 0.2 },
    smell_absolute: { enabled: false, value: 5 },
    smell_relative: { enabled: false, Y: 1, L: 1 },
  };
  const [thresholds, setThresholds] = useState(initialThresholds);
  useEffect(() => {
    document.title = isEditMode ? "DCode | Edit Quality Gate" : "DCode | New Quality Gate";

    const fetchProfileDetails = async () => {
      if (!originalProfileId) return;

      try {
        const response = await fetch(`${API_BASE_URL}/quality-gate/${originalProfileId}/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          // If this error fires, double-check your Django URL configuration!
          throw new Error("Failed to fetch profile details.");
        }

        const data = await response.json();
        const profileData = data.data; // Ensure this matches your backend response structure

        // 1. Populate Name
        setProfileName(profileData.name);

        // 2. Populate Metric Type
        setSelectedMetric(profileData.metric_type);

        // 3. Populate Thresholds
        const fetchedThresholds = profileData.thresholds || {};

        setThresholds({
          fitness_absolute: fetchedThresholds.fitness_absolute || initialThresholds.fitness_absolute,
          fitness_relative: fetchedThresholds.fitness_relative || initialThresholds.fitness_relative,
          smell_absolute: fetchedThresholds.smell_absolute || initialThresholds.smell_absolute,
          smell_relative: fetchedThresholds.smell_relative || initialThresholds.smell_relative,
        });

      } catch (err) {
        console.error("Error fetching quality gate profile:", err);
        setAlertMessage("Failed to load profile for editing: " + err.message);
        setAlertType("danger");
      }
    };

    if (isEditMode) {
      fetchProfileDetails();
    } else {
      // Reset/Ensure creation mode defaults
      setProfileName("");
      setSelectedMetric(null);
      setThresholds(initialThresholds);
      setCurrentStep(0);
    }
  }, [API_BASE_URL, originalProfileId, isEditMode]);

  const isLargeScreen = windowWidth >= 992;
  const getSidebarWidth = () => (!sidebarOpen ? "0px" : sidebarCollapsed ? "80px" : "280px");

  // Dynamic steps
  const steps = [
    { name: "Profile name", id: "name" },
    { name: "Select metric", id: "select" },
    { name: "Configure thresholds", id: "config" },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSaveProfile = async () => {
    // 1. Validation (remains the same)
    if (!profileName.trim() || !selectedMetric) {
      setAlertMessage("Profile name and metric selection are required.");
      setAlertType("danger");
      return;
    }

    // Check if at least one threshold is enabled
    const absKey = selectedMetric === "fitness" ? "fitness_absolute" : "smell_absolute";
    const relKey = selectedMetric === "fitness" ? "fitness_relative" : "smell_relative";

    if (!thresholds[absKey].enabled && !thresholds[relKey].enabled) {
      setAlertMessage("You must enable at least one threshold (Absolute or Relative).");
      setAlertType("danger");
      return;
    }

    const projectId = localStorage.getItem("selectedProjectId");

    setSaving(true);
    setAlertMessage(null); // Clear previous alerts

    // 2. Prepare the payload (remains the same)
    const payload = {
      profileName: profileName.trim(),
      selectedMetric: selectedMetric,
      thresholds: thresholds,
    };

    let url;
    let method;
    let successMessage;

    if (isEditMode) {
      // Assuming an endpoint for updating a profile: /quality-gate/:profileId/update/
      url = `${API_BASE_URL}/quality-gate/${originalProfileId}/update/`;
      method = 'PUT'; // Or 'PATCH' if your backend accepts it
      successMessage = "Quality Gate Profile updated successfully!";
    } else {
      // Creation endpoint
      url = `${API_BASE_URL}/quality-gate/${projectId}/create-quality-gate/`;
      method = 'POST';
      successMessage = "Quality Gate Profile saved successfully!";
    }


    try {
      console.log("Saving to Backend URL:", url);
      console.log("Payload:", payload);

      const response = await fetch(url, {
        method: method, // Use dynamic method
        headers: {
          'Content-Type': 'application/json',

        },
        body: JSON.stringify(payload),
      });

      // 4. Handle the response
      if (response.ok) {
        const result = await response.json();
        setAlertMessage(successMessage);
        setAlertType("success");
        console.log("Backend Response:", result);

        navigate(`/project/${projectId}/settings`); // Navigate back to the settings page
      } else {
        // Read error message from backend
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Server responded with status ${response.status}.`;

        setAlertMessage(`Failed to save profile: ${errorMessage}`);
        setAlertType("danger");
      }
    } catch (e) {
      console.error("Fetch error:", e);
      setAlertMessage("Failed to connect to the server. Please check your network.");
      setAlertType("danger");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- Step Components (Step1_ProfileName, Step2_SelectMetric, Step3_Configure remain the same) ---
  

  const Step2_SelectMetric = () => (
    <div className="p-4">
      {/* <h3 className="text-dark fw-bold mb-5">2. Choose the Core Metric to Enforce</h3> */}
      {/* <p className="text-muted mb-4">Select **one** core metric that this quality gate will monitor and enforce rules for.</p> */}

      <div className="row g-4 justify-content-center">
        {/* Fitness Score Card */}
        <div className="col-lg-5 col-md-6">
          <div
            className={`card h-100 p-3 shadow-sm ${selectedMetric === "fitness" ? "border-3" : "border-light"}`}
            onClick={() => setSelectedMetric("fitness")}
            style={{
              cursor: "pointer",
              transition: "all 0.3s ease-in-out",
              // Custom active style
              borderColor: selectedMetric === "fitness" ? DCODE_PRIMARY : '',
              boxShadow: selectedMetric === "fitness" ? `0 0 10px ${DCODE_PRIMARY}50` : 'none'
            }}
          >
            <div className="card-body text-center">
              {/* <TrendingUp size={48} className="text-success mb-3" /> */}
              <h5 className="card-title fw-bold">Fitness score</h5>
              <p className="text-muted small mb-3">Higher is better • The score represents the overall code health and quality.</p>
              <div
                className={`badge ${selectedMetric === "fitness" ? "text-white" : "bg-secondary-subtle text-secondary"}`}
                style={{ backgroundColor: selectedMetric === "fitness" ? DCODE_PRIMARY : '' }}
              >
                {selectedMetric === "fitness" ? <>Selected <Check size={14} className="ms-1" /></> : "Click to Select"}
              </div>
            </div>
          </div>
        </div>

        {/* Code Smell Density Card */}
        <div className="col-lg-5 col-md-6">
          <div
            className={`card h-100 p-3 shadow-sm ${selectedMetric === "smell" ? "border-3" : "border-light"}`}
            onClick={() => setSelectedMetric("smell")}
            style={{
              cursor: "pointer",
              transition: "all 0.3s ease-in-out",
              // Custom active style
              borderColor: selectedMetric === "smell" ? DCODE_PRIMARY : '',
              boxShadow: selectedMetric === "smell" ? `0 0 10px ${DCODE_PRIMARY}50` : 'none'
            }}
          >
            <div className="card-body text-center">
              {/* <TrendingDown size={48} className="text-danger mb-3" /> */}
              <h5 className="card-title fw-bold">Code smell density</h5>
              <p className="text-muted small mb-3">Lower is better • Density measures the number of code quality smells per thousand lines of code (KLOC), weighted across architecture, design, and implementation smells.</p>
              <div
                className={`badge ${selectedMetric === "smell" ? "text-white" : "bg-secondary-subtle text-secondary"}`}
                style={{ backgroundColor: selectedMetric === "smell" ? DCODE_PRIMARY : '' }}
              >
                {selectedMetric === "smell" ? <>Selected <Check size={14} className="ms-1" /></> : "Click to Select"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  const renderWizardStepper = () => (
    <div className="d-flex justify-content-center align-items-center mb-4">
      {steps.map((step, index) => {
        const isActive = currentStep === index;
        // Allows clicking steps 0, 1, and 2 if in Edit Mode
        const isComplete = currentStep > index || (isEditMode && index < 2);

        const circleColor = isActive || isComplete ? DCODE_PRIMARY : '#dee2e6';
        const textColor = isActive ? DCODE_PRIMARY : isComplete ? DCODE_PRIMARY : '#6c757d';

        return (
          <React.Fragment key={step.id}>
            <div
              className="d-flex flex-column align-items-center"
              // Allow jumping to any step if in edit mode, otherwise only completed steps
              style={{ cursor: isComplete || isEditMode ? 'pointer' : 'default' }}
              onClick={() => (isComplete || isEditMode) && setCurrentStep(index)}
            >
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center border`}
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: isActive || isComplete ? DCODE_PRIMARY : 'white',
                  borderColor: circleColor,
                  color: isComplete || isActive ? 'white' : '#6c757d',
                  fontWeight: 'bold',
                }}
              >
                {isComplete ? <Check size={20} /> : index + 1}
              </div>
              <small className={`mt-2 fw-bold text-center`} style={{ width: '100px', color: textColor }}>
                {step.name}
              </small>
            </div>
            {index < steps.length - 1 && (
              <div
                className="flex-grow-1 mx-3"
                style={{
                  height: '2px',
                  backgroundColor: isComplete ? DCODE_PRIMARY : '#dee2e6',
                  transition: 'background-color 0.3s',
                  maxWidth: '100px'
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="d-flex flex-column min-vh-100">
      <div style={{ marginLeft: isLargeScreen ? getSidebarWidth() : "0px", transition: "margin-left 0.4s", position: "relative", zIndex: 1030 }}>
        <Header sidebarOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          isCollapsed={sidebarCollapsed} userDropdownOpen={userDropdownOpen} toggleUserDropdown={() => setUserDropdownOpen(!userDropdownOpen)} />
      </div>

      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        activeItem={activeSidebarItem} onItemClick={() => { }} onCollapseChange={setSidebarCollapsed} />

      <main style={{ marginLeft: isLargeScreen ? getSidebarWidth() : "0px", paddingTop: "70px", minHeight: "calc(100vh - 140px)" }}>
        <div className="container-fluid p-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h2 className="mb-0 text-dark fw-bold">{isEditMode ? `Edit profile: ${profileName || 'Loading...'}` : "Quality gate profile"}</h2>
            <button
              className="btn btn-outline-secondary d-flex align-items-center"
              onClick={() => navigate(`/project/${projectId}/settings`)}
            >
              <ArrowLeft size={18} className="me-1" /> Back
            </button>
          </div>

          <div className="card shadow-lg border-0 mb-4">
            <div className="card-header bg-white border-bottom p-4">
              {renderWizardStepper()}
            </div>

            <div className="card-body" style={{ minHeight: '400px' }}>
              {currentStep === 0 && <Step1_ProfileName profileName={profileName} setProfileName={setProfileName} />}
              {currentStep === 1 && <Step2_SelectMetric selectedMetric={selectedMetric} setSelectedMetric={setSelectedMetric} />}
              {currentStep === 2 && <Step3_Configure thresholds={thresholds} setThresholds={setThresholds} selectedMetric={selectedMetric} />}
            </div>

            {/* Global Alert Message (remains the same) */}
            {alertMessage && (
              <div
                className={`alert alert-${alertType === "success" ? 'success' : 'danger'} mx-4`} // Keep default Bootstrap class for styling alert box
                role="alert"
                // Override text color to use the custom primary color for success
                style={{
                  color: alertType === "success" ? DCODE_PRIMARY : DCODE_DANGER,
                  borderColor: alertType === "success" ? DCODE_PRIMARY : DCODE_DANGER,
                  backgroundColor: alertType === "success" ? `${DCODE_PRIMARY}10` : `${DCODE_DANGER}10`, // Light background tint
                }}
              >
                {alertType === "success" ? <Check size={18} className="me-2" style={{ color: DCODE_PRIMARY }} /> : null}
                {alertMessage}
              </div>
            )}

            <div className="card-footer bg-white d-flex justify-content-between p-3 border-top">
              <button className="btn btn-outline-secondary" onClick={handlePrev} disabled={currentStep === 0}>
                <ArrowLeft size={18} className="me-1" /> Previous step
              </button>

              {currentStep < 2 ? (
                <button
                  className="btn d-flex align-items-center text-white"
                  onClick={handleNext}
                  disabled={
                    (currentStep === 0 && !profileName.trim()) ||
                    (currentStep === 1 && !selectedMetric)
                  }
                  style={{ backgroundColor: DCODE_PRIMARY, border: `1px solid ${DCODE_PRIMARY}` }}
                >
                  Next step <ArrowLeft size={18} className="ms-1" style={{ transform: 'scaleX(-1)' }} />
                </button>
              ) : (
                <button
                  className="btn d-flex align-items-center text-white"
                  onClick={handleSaveProfile}
                  disabled={saving || !profileName.trim() || !selectedMetric}
                  style={{ backgroundColor: DCODE_PRIMARY, border: `1px solid ${DCODE_PRIMARY}` }}
                >
                  {saving ? <><Loader2 size={18} className="me-1 animate-spin" /> Saving Profile...</> : <><Save size={18} className="me-1" /> {isEditMode ? "Update Profile" : "Save Profile"}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default QualityGate;